/**
 * applyMockDemoBootstrap.ts — Restaura datos demo en localStorage cuando faltan
 * o quedaron corruptos/vacíos (p. ej. tras mock_data_version en main.tsx).
 *
 * NO borra datos reales del usuario: solo rellena claves vacías o inválidas
 * en modo demo (mock-token / altenot_demo_mode).
 */
import { txReadRaw, txWriteRaw } from "@/lib/persistence/transactionalStore";
import { parsePersistedValue } from "@/mocks/helpers/storage";
import { defaultProducts } from "@/mocks/data/products";
import { defaultEmployees, defaultAttendance } from "@/mocks/data/employees";
import { defaultStockMovements } from "@/mocks/data/stockMovements";
import { defaultTasks } from "@/mocks/data/tasks";
import { buildDefaultTiendaPedidos } from "@/mocks/data/defaultTiendaPedidos";
import { DEFAULT_TIENDA_CONFIG } from "@/features/tienda/constants/defaultTiendaConfig";
import {
  FIDELIZACION_DEFAULT_CLIENTES,
  FIDELIZACION_DEFAULT_CONFIG,
  FIDELIZACION_DEFAULT_MOVIMIENTOS,
  FIDELIZACION_DEFAULT_PREMIOS,
} from "@/mocks/data/fidelizacionDefaults";

function isDemoEnvironment(): boolean {
  try {
    if (txReadRaw("altenot_demo_mode") === "true") return true;
    const authRaw = txReadRaw("altenot_auth");
    if (authRaw?.includes("mock-token")) return true;
    const token = txReadRaw("token");
    if (token?.includes("mock-token")) return true;
  } catch {
    /* ignore */
  }
  return false;
}

function readParsed(key: string): unknown {
  try {
    const raw = txReadRaw(key);
    if (!raw) return undefined;
    // Los valores grandes (inventory_products con 300+ productos) se guardan
    // COMPRIMIDOS con prefijo `lz1:` (ver mocks/helpers/storage). Un
    // JSON.parse crudo sobre ese prefijo lanza y provocaba que el bootstrap
    // re-sembrara los defaults en CADA arranque, borrando descuento/IVA/stock
    // editados por el usuario. parsePersistedValue maneja ambos formatos.
    return parsePersistedValue(raw) as unknown;
  } catch {
    return undefined;
  }
}

function ensureObject(key: string, fallback: Record<string, unknown>): void {
  const parsed = readParsed(key);
  if (
    parsed &&
    typeof parsed === "object" &&
    !Array.isArray(parsed) &&
    Object.keys(parsed as object).length > 0
  ) {
    return;
  }
  txWriteRaw(key, JSON.stringify(fallback));
}

function ensureArray<T>(
  key: string,
  fallback: T[],
  reseedIfEmpty = true,
): void {
  const parsed = readParsed(key);
  if (Array.isArray(parsed) && parsed.length > 0) return;
  if (Array.isArray(parsed) && parsed.length === 0 && !reseedIfEmpty) return;
  txWriteRaw(key, JSON.stringify(fallback));
}

/** Claves que main.tsx puede limpiar al subir mock_data_version. */
export const MOCK_DATA_RESET_KEYS = [
  "tienda_config",
  "tienda_logo",
  "inventory_products",
  "tienda_pedidos",
  "inventory_stock_movements",
  "caja_ventas",
  "caja_estado",
  "fidelizacion_config",
  "fidelizacion_clientes",
  "fidelizacion_movimientos",
  "fidelizacion_premios",
  "personal_tareas_v4",
  // v21+: re-siembra el histórico de asistencia demo ampliado (toda la nómina,
  // dos semanas y fichajes de hoy). Un cambio de versión lo regenera.
  "personal_asistencia",
  // v19+: limpia logs demo que pudieron quedar de versiones anteriores
  "auditoria_logs_v1",
] as const;

export function applyMockDemoBootstrap(options?: {
  force?: boolean;
  /** Tras borrar claves por versión: siempre re-sembrar esas claves. */
  afterVersionReset?: boolean;
}): void {
  if (!options?.force && !options?.afterVersionReset && !isDemoEnvironment()) {
    return;
  }

  ensureObject(
    "tienda_config",
    DEFAULT_TIENDA_CONFIG as unknown as Record<string, unknown>,
  );

  // reseedIfEmpty=false: un array vacío es un estado legítimo (el usuario
  // eliminó todos sus productos demo con «Eliminar datos de prueba» y no tiene
  // productos propios). Re-sembrar aquí reintroduciría los 363 productos demo
  // en cada recarga. Solo se re-siembra si la clave FALTA (instalación nueva
  // o reset de versión) o está corrupta (no es un array).
  ensureArray(
    "inventory_products",
    defaultProducts as unknown as Record<string, unknown>[],
    false,
  );
  ensureArray(
    "personal_empleados",
    defaultEmployees as unknown as Record<string, unknown>[],
    true,
  );
  ensureArray(
    "personal_tareas_v4",
    defaultTasks as unknown as Record<string, unknown>[],
    true,
  );
  ensureArray(
    "tienda_pedidos",
    buildDefaultTiendaPedidos() as unknown as Record<string, unknown>[],
    true,
  );
  ensureArray(
    "inventory_stock_movements",
    defaultStockMovements as unknown as Record<string, unknown>[],
    !options?.afterVersionReset,
  );

  ensureObject(
    "fidelizacion_config",
    FIDELIZACION_DEFAULT_CONFIG as unknown as Record<string, unknown>,
  );
  ensureArray(
    "fidelizacion_clientes",
    FIDELIZACION_DEFAULT_CLIENTES as unknown as Record<string, unknown>[],
    true,
  );
  ensureArray(
    "fidelizacion_movimientos",
    FIDELIZACION_DEFAULT_MOVIMIENTOS as unknown as Record<string, unknown>[],
    !options?.afterVersionReset,
  );
  ensureArray(
    "personal_tareas_v4",
    defaultTasks as unknown as Record<string, unknown>[],
    true,
  );
  ensureArray(
    "personal_asistencia",
    defaultAttendance as unknown as Record<string, unknown>[],
    true,
  );
  ensureArray(
    "fidelizacion_premios",
    FIDELIZACION_DEFAULT_PREMIOS as unknown as Record<string, unknown>[],
    !options?.afterVersionReset,
  );
}
